import { useEffect, useState } from 'react';

import { AssignableGearbeltRole, GetUsersResponseDTO, PermissionRoles } from '@gearbelt/global';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toaster';
import { userKeys } from '@/queries/users';
import { ApiService } from '@/services/api-v2';

const ROLE_OPTIONS: Array<{ value: AssignableGearbeltRole; label: string }> = [
  { value: PermissionRoles.GearbeltAdmin, label: 'Admin' },
  { value: PermissionRoles.GearbeltMember, label: 'Member' },
];

export function RoleCell({
  userId,
  role,
  editable = true,
}: {
  userId: string;
  role: PermissionRoles | null;
  /** When false, render a disabled dropdown instead of an editable one. */
  editable?: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [optimisticRole, setOptimisticRole] = useState<PermissionRoles | null>(role);

  // Keep the optimistic value in sync if the parent hands us a fresh `role`
  // (e.g. cache invalidation after another row's mutation refetches the list).
  useEffect(() => {
    setOptimisticRole(role);
  }, [role]);

  const { mutate, isPending } = useMutation({
    mutationFn: ({ nextRole }: { nextRole: AssignableGearbeltRole }) => ApiService.UpdateUserRole({ userId, role: nextRole }),
    onMutate: async ({ nextRole }) => {
      const previous = optimisticRole;
      setOptimisticRole(nextRole);
      return { previous };
    },
    onSuccess: (_data, { nextRole }) => {
      toast.success('Role updated');
      // Patch the Users infinite-query cache in place instead of invalidating
      // the whole list — invalidating triggers a full network refetch and
      // scroll/position reset on a table that may be hundreds of rows deep.
      // `getContactsInfiniteQueryOptions` also lives under userKeys.all
      // with a different key shape; the predicate's `'infinite'` segment
      // picks only the Users list, not contacts.
      queryClient.setQueriesData<InfiniteData<GetUsersResponseDTO, string | undefined>>(
        {
          predicate: (query) => query.queryKey[0] === userKeys.all[0] && query.queryKey[1] === 'infinite',
        },
        (data) => {
          if (!data) return data;
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              data: page.data.map((row) => (row.id === userId ? { ...row, roles: [nextRole] } : row)),
            })),
          };
        },
      );
    },
    onError: (error: Error, _vars, context) => {
      if (context) setOptimisticRole(context.previous);
      toast.error(error.message || 'Failed to update role');
    },
  });

  const handleChange = (next: AssignableGearbeltRole) => {
    if (next === optimisticRole) return;
    mutate({ nextRole: next });
  };

  // Ensure the currently-held role always renders, even when it isn't one of
  // the assignable options (e.g. a SitemateSuperUser). The fallback option is
  // display-only — the Select is disabled in that case so `onChange` never
  // fires with a non-assignable role, which is what keeps the cast below
  // safe at runtime.
  const selected: { value: AssignableGearbeltRole; label: string } | null =
    ROLE_OPTIONS.find((opt) => opt.value === optimisticRole) ??
    (optimisticRole ? { value: optimisticRole as AssignableGearbeltRole, label: optimisticRole } : null);

  const isNonAssignableRole = Boolean(optimisticRole && !ROLE_OPTIONS.some((opt) => opt.value === optimisticRole));
  const isDisabled = !editable || isPending || isNonAssignableRole;

  return (
    // Wrapper absorbs the row's click handler so clicking the dropdown doesn't
    // navigate to the user detail. `role="none"` marks it as non-interactive
    // so assistive tech doesn't pick it up — the real interactive surface is
    // the Select inside.
    <div role="none" onClick={(e) => e.stopPropagation()}>
      <Select aria-label="Role" value={selected} options={ROLE_OPTIONS} isDisabled={isDisabled} onChange={(option) => option && handleChange(option.value)} />
    </div>
  );
}
